// Styles for the shared WindowFrame (variant-based: event / account / plain).
import styled, {css as styledCss} from 'styled-components';
import {BLACK, LIGHT_GRAY, WHITE, dFlexCenter, mobileMedia, desktopMedia} from '../../../../globalVariables/GlobalVariables';
import zIndex from '../../../../globalVariables/zIndex';
import {ex, whiteEx} from '../../../../assets/symbols/symbols/Symbols.css';
import {popUpBody} from '../../../../component/newPopup/mobilePopup/MobilePopup.css';
import {List} from '../../../../component/list/List.css';
import {EventMenuForm} from '../../../../layout/eventMenu/EventMenu.css';
import {windowBase} from '../../style.css';
import {getBackground, getColor} from '../../utils/themeUtils';
import realtimeFonts from '../../fonts';

// 'event' = original event window box (unchanged — keeps event windows pixel-identical).
const eventBase = styledCss<{height?: string}>`
    height: ${props=>props.height || '265px'};
    &  *:not(.free-color *):not(.free-color) {
      color: ${props=>props.theme.isDarkMode && LIGHT_GRAY};
    }
    & .close.pop {
      background-image: url("${props=>props.theme.isDarkMode?whiteEx: ex}");
    }
    &.transactions {
      height: 100%;
    }
    ${mobileMedia}{
        max-width: calc(100vw - 12px);
      &:not(.cash){
      height: fit-content;
      }
    }
    ${popUpBody}{
      height:0;
      min-height: 0;
      background-color: ${props=>getBackground(props.theme.isDarkMode)};
      &.show{
        min-height: 30%;
        height: fit-content;
      }
    }
`;
// 'account' = account window body (padded flex column, auto height) for the 'row' header model.
const accountBase = styledCss<{height?: string}>`
    height: ${props=>props.height || 'auto'};
    min-height: 200px;
    padding: var(--space-lg);
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
`;
// 'plain' = neutral frame only (consumer supplies its own inner layout, e.g. WindowReports).
const plainBase = styledCss<{height?: string}>`
    height: ${props=>props.height};
`;

export const windowWrapper = styled.div<{ gridArea?: string; height?: string; $variant?: 'event' | 'account' | 'plain'; }>`
    grid-area: ${props=>props.gridArea};
    width: 100%;
    position: relative;
    cursor: auto;
    ${windowBase}
    ${p => p.$variant === 'account' ? accountBase : p.$variant === 'plain' ? plainBase : eventBase}
`;

export const explainWindowContent = styled.div`
     overflow: hidden;
     height: 80%;
     white-space: break-spaces;
`;
export const explainContainer = styled.div<{isOpen?:boolean, isDarkMode?: boolean}>`
    width: ${props => props.isOpen?'100%':'0'};
    opacity: ${props => props.isOpen?'1':'0'};
    transition: opacity 300ms${props => props.isOpen?'':', width 0ms 300ms'};
    height: 100%;
    background-color: ${props => getBackground(props.theme.isDarkMode)};
    border-radius: 15px;
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline-end: 0;
    align-items: center;
    z-index: ${zIndex.baseHigh};
    ${mobileMedia}{
      position: unset;
    }
`;

// $variant 'event' = original absolute icon cluster (top-right). 'account' = static header row (title | actions + "?").
export const windowHeader = styled.div<{ openLayout?: boolean, $variant?: 'event' | 'account' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: ${zIndex.baseRaised};
  ${p => p.$variant === 'account' ? `
    justify-content: space-between;
    width: 100%;
  ` : `
    font-size: 18px;
    margin: 22px 20px 10px 20px;
    justify-content: space-between;
    width: fit-content;
    position: absolute;
    inset-inline-end: 0;
  `}
  .promotionSummary & {
    ${desktopMedia}{
      /* this card adds its own border (space-sm) + padding (space-2xl); inset the ? to match its content corner */
      margin-top: calc(var(--space-sm) + var(--space-2xl));
      margin-inline-end: calc(var(--space-sm) + var(--space-2xl));
    }
  }
  ${List}.show {
    ${p=>p.openLayout&&'height: 260px;'}
  }
  .layoutParent {
    border-bottom: 0;
    height: 40px;
    &.arrow{
      &::after {
        transition: all 100ms;
      }
      &.open::after {
        transform: rotate(0);
      }
    }

  }
  .itemContent .itemIcon {
    background-size: 18px;

  }
  & .layoutItem {
    border-bottom: 0;
    height: 20px;
  }
`;
// Title + actions cluster for the account header row.
export const title = styled.div`
    ${realtimeFonts.windowTitle};
    color: ${p => getColor(p.theme.isDarkMode)};
`;
export const actions = styled.div`
    display: flex;
    align-items: center;
    gap: var(--space-sm);
`;
// Scrollable wrapper for the react-share Helper content (long account explanations).
export const helperContent = styled.div`
    white-space: pre-line;
    text-align: start;
    max-height: 40vh;
    overflow-y: auto;
`;
export const linkButton = styled(EventMenuForm)`
`;
export const howArrivedText = styled.div`
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: all 300ms;
  &:hover {
    text-decoration: underline;
  }
`;
export const questionIcon = styled.button`
  ${dFlexCenter};
  background-color: ${p=>p.theme.isDarkMode?LIGHT_GRAY :BLACK};
  color:  ${p=>p.theme.isDarkMode? '#1E1E1E':WHITE};
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 16px;
  font-weight: bold;
  z-index: ${zIndex.baseHigh};
  cursor: pointer;
  &:focus {
    outline: none;
  }
`;
export const questionIconMobile = styled(questionIcon)`
    width: 60px;
    height: 60px;
    font-size: 40px;
    margin-top: -90px;
    position: absolute;
    inset-inline-end: calc(50% - 30px);
`;
export const explainWindowTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
  margin: 20px 35px 0 50px;
  gap: 5px;
  `;
export const backArrow = styled.button`
  display: inline-block;
  margin-inline-end: 8px;
  font-size: 20px;
  background-color: transparent;
  border: none;
  top: 19px;
  inset-inline-start: 23px;
  cursor: pointer;
   ${p=>p.theme.rtl?'': 'rotate: 180deg;'}
`;
